import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DateField } from "@/components/ui/date-field";
import { toast } from "sonner";
import { fmtDate, photoSrc } from "@/lib/format";
import { FileText, Plus, Eye, Trash2, Upload, Loader2 } from "lucide-react";

const SRC = { upload: ["Document", "bg-[#0F1E3D]/10 text-[#0F1E3D]"], brief: ["Brief", "bg-blue-50 text-blue-700"], contract: ["Contract", "bg-[#C9A227]/15 text-[#7A5F0E]"], overeenkomst: ["Overeenkomst", "bg-emerald-50 text-emerald-700"] };
const today = () => new Date().toISOString().slice(0, 10);
export const DOC_CATEGORIES = ["Contract", "Brief", "Factuur", "Betaalbewijs", "Identiteitsbewijs", "Correspondentie", "Bewijsstuk", "Juridisch", "Overig"];
const GUESS = [[/contract|overeenkomst|huur|lening/i, "Contract"], [/factuur|invoice|nota/i, "Factuur"], [/betaal|bank|afschrift|kwitantie/i, "Betaalbewijs"], [/paspoort|id[-_ ]?kaart|identiteit|rijbewijs/i, "Identiteitsbewijs"], [/brief|aanmaning|sommatie|herinnering/i, "Brief"], [/mail|bericht|correspond/i, "Correspondentie"], [/dagvaard|vonnis|deurwaard|advocaat/i, "Juridisch"], [/foto|bewijs|\.(jpe?g|png|heic)$/i, "Bewijsstuk"]];
const guessCat = (n) => (GUESS.find(([re]) => re.test(n)) || [null, "Overig"])[1];

export const DocumentsTab = ({ tenantId = "", clientId = "", onChanged }) => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("alle");
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    api.get("/documents", { params: { tenant_id: tenantId, client_id: clientId } }).then((r) => setRows(r.data)).catch(() => toast.error("Documenten laden mislukt"));
  }, [tenantId, clientId]);
  useEffect(() => { load(); }, [load]);

  const view = async (d) => {
    if (d.file_url) { window.open(photoSrc(d.file_url), "_blank"); return; }
    try {
      const res = await api.get(d.pdf_api, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("Openen mislukt"); }
  };
  const del = async (d) => {
    if (!window.confirm(`"${d.title}" verwijderen?`)) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Document verwijderd"); load(); onChanged?.(); } catch { toast.error("Verwijderen mislukt"); }
  };
  const counts = rows.reduce((m, r) => ({ ...m, [r.source]: (m[r.source] || 0) + 1 }), {});
  const shown = filter === "alle" ? rows : rows.filter((r) => r.source === filter);

  return (
    <Card className="p-5" data-testid="documents-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-heading font-semibold flex items-center gap-2 text-[#0F1E3D]"><FileText className="h-4 w-4 text-[#C9A227]" /> Documenten ({rows.length})</h3>
        <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white border border-[#C9A227]" onClick={() => setOpen(true)} data-testid="documents-add-button"><Plus className="h-4 w-4 mr-1" /> Document toevoegen</Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {[["alle", "Alle", rows.length], ...Object.keys(SRC).filter((k) => counts[k]).map((k) => [k, SRC[k][0], counts[k]])].map(([k, l, n]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`documents-filter-${k}`}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${filter === k ? "bg-[#0F1E3D] text-white border-[#C9A227]" : "bg-white text-slate-600 hover:border-[#C9A227]/60"}`}>{l} ({n})</button>
        ))}
      </div>
      {shown.length === 0 ? <p className="text-sm text-slate-400 text-center py-8" data-testid="documents-empty">Nog geen documenten.</p> : (
        <div className="divide-y">
          {shown.map((d) => <DocRow key={`${d.source}-${d.id}`} d={d} onView={view} onDelete={del} />)}
        </div>
      )}
      <AddDocDialog open={open} onOpenChange={setOpen} tenantId={tenantId} clientId={clientId} onSaved={() => { setOpen(false); load(); onChanged?.(); }} />
    </Card>
  );
};

const DocRow = ({ d, onView, onDelete }) => {
  const [l, c] = SRC[d.source] || SRC.upload;
  return (
    <div className="flex items-center justify-between gap-3 py-3" data-testid={`document-row-${d.id}`}>
      <div className="min-w-0 flex items-start gap-3">
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 mt-0.5 ${c}`}>{l}</span>
        <div className="min-w-0">
          <div className="font-medium text-sm text-[#0F1E3D] truncate">{d.title}</div>
          <div className="text-xs text-slate-400">{d.category ? `${d.category} · ` : ""}{d.date ? fmtDate(d.date) : "—"}{d.note ? ` · ${d.note}` : ""}</div>
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button size="sm" variant="ghost" onClick={() => onView(d)} title="Bekijken" data-testid={`document-view-${d.id}`}><Eye className="h-4 w-4" /></Button>
        {d.deletable && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => onDelete(d)} title="Verwijderen" data-testid={`document-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
};

const AddDocDialog = ({ open, onOpenChange, tenantId, clientId, onSaved }) => {
  const empty = { title: "", date: today(), note: "", category: "Overig", file_url: "", file_name: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [catTouched, setCatTouched] = useState(false);
  const ref = useRef(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => { if (open) { setF(empty); setCatTouched(false); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    if (!file) { return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = (await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
      const mod = file.lastModified ? new Date(file.lastModified).toISOString().slice(0, 10) : today();
      const auto = (n) => n.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ");
      setF((x) => ({ ...x, file_url: up.url, file_name: file.name, title: !x.title || (x.file_name && x.title === auto(x.file_name)) ? auto(file.name) : x.title, date: mod, category: catTouched ? x.category : guessCat(file.name) }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Uploaden mislukt"); }
    if (ref.current) { ref.current.value = ""; }
    setBusy(false);
  };
  const save = async () => {
    setBusy(true);
    try { await api.post("/documents", { ...f, tenant_id: tenantId, client_id: clientId }); toast.success("Document toegevoegd"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="documents-add-dialog">
        <DialogHeader><DialogTitle>Document toevoegen</DialogTitle><DialogDescription>Kies een bestand; titel en datum worden automatisch ingevuld.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <input ref={ref} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="documents-file-input" />
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="documents-upload-button"
            className="w-full flex items-center gap-3 border-2 border-dashed border-[#C9A227]/50 rounded-xl p-4 hover:bg-[#C9A227]/5 text-left">
            {busy ? <Loader2 className="h-5 w-5 animate-spin text-[#C9A227]" /> : <Upload className="h-5 w-5 text-[#C9A227]" />}
            <span className="text-sm">{f.file_name ? <><b>{f.file_name}</b><br /><span className="text-xs text-slate-500">Klik om te vervangen</span></> : <><b>Bestand kiezen</b><br /><span className="text-xs text-slate-500">PDF, Word, afbeelding of ander bestand</span></>}</span>
          </button>
          <div><Label>Titel</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="documents-title-input" /></div>
          <div>
            <Label>Categorie</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="documents-category">
              {DOC_CATEGORIES.map((c) => (
                <button key={c} type="button" onClick={() => { set("category", c); setCatTouched(true); }} data-testid={`documents-category-${c.toLowerCase()}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${f.category === c ? "bg-[#0F1E3D] text-white border-[#C9A227]" : "bg-white text-slate-600 hover:border-[#C9A227]/60"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div><Label>Datum</Label><DateField testId="documents-date-input" value={f.date} onChange={(v) => set("date", v)} /></div>
          <div><Label>Notitie (optioneel)</Label><Textarea rows={2} value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="documents-note-input" /></div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
            <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" disabled={busy || !f.file_url || !f.title.trim() || !f.date} onClick={save} data-testid="documents-save-button">Opslaan</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
